import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, Observer} from 'rxjs';

import {Facebook} from '../sdk/facebook.sdk';
import {FacebookConfig} from '../config/facebook.config';
import {AuthEventsEmitter} from '../events/auth.events';
import {StorageService} from './storage.service';
import {FacebookInfoDTO} from '../dto/facebookInfo.dto';

declare const FB: Facebook.FacebookStatic;

@Injectable()
export class SocialService {
    private readonly FACEBOOK_ID: string = 'FACEBOOK_ID';

    facebookId: string;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly facebookConfig: FacebookConfig,
        private readonly storageService: StorageService,
        private readonly authEventsEmitter: AuthEventsEmitter
    ) {
        this.initFacebook();
        this.initFacebookSDK();

        this.facebookId = this.getFacebookId();

        this.authEventsEmitter.facebookIdChanged$
            .subscribe(facebookId => {
                this.facebookId = facebookId;

                this.setFacebookId(facebookId);
            });

        this.authEventsEmitter.tokenChanged$
            .subscribe(token => {
                if (!token) {
                    delete this.facebookId;

                    this.deleteFacebookId();
                }
            });
    }

    initFacebook() {
        (window as typeof window & { fbAsyncInit: () => void; }).fbAsyncInit = () => {
            this.initFacebookSDK();
        };
    }

    initFacebookSDK(): void {
        try {
            FB.init(this.facebookConfig);
        } catch (e) {
            // we do not really need to handle this error
        }
    }

    openFacebookLoginDialog(): Observable<Facebook.StatusResponse> {
        return new Observable((observer: Observer<Facebook.StatusResponse>) => {
            FB.login((response: Facebook.StatusResponse) => {
                if (response.authResponse) {
                    this.authEventsEmitter.broadcastFacebookIdChanged(response.authResponse.userID);

                    observer.next(response);
                } else {
                    observer.error(response);
                }

                observer.complete();
            }, {
                scope: 'email'
            } as Facebook.LoginOptions);
        });
    }

    getFacebookProfilePictureUrl(userID: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            this.httpClient.get<{ data: { url: string; } }>(`https://graph.facebook.com/${userID}/picture?type=square&redirect=0`)
                .subscribe(({data: {url}}) => {
                    observer.next(url);
                    observer.complete();
                }, err => {
                    observer.error(err);
                });
        });
    }

    getFacebookInfo(userID: string): Observable<FacebookInfoDTO> {
        return new Observable((observer: Observer<FacebookInfoDTO>) => {
            FB.api(`/${userID}/?fields=email`, 'get', {}, (({email, first_name, last_name}) => {
                observer.next({
                    email,
                    first_name,
                    last_name
                });
                observer.complete();
            }));
        });
    }

    getFacebookFriends(userID: string, accessToken: string): Observable<void> {
        return new Observable((observer: Observer<void>) => {
            FB.api(`/${userID}/friends?access_token=${accessToken}`, 'get', {}, ({data}) => {
                observer.next(null);
                observer.complete();
            });
        });
    }

    getFacebookId(): string {
        return this.storageService.getItem(this.FACEBOOK_ID);
    }

    setFacebookId(facebookId: string): void {
        this.storageService.setItem(this.FACEBOOK_ID, facebookId);
    }

    deleteFacebookId() {
        this.storageService.removeItem(this.FACEBOOK_ID);
    }

    get userIsLoggedInFacebook(): boolean {
        return !!this.facebookId;
    }
}
